import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";

export const RESIDENT_STATUSES = ["new", "active", "hospital", "discharged"] as const;
export type ResidentStatus = (typeof RESIDENT_STATUSES)[number];

export const RESIDENT_STATUS_LABELS: Record<ResidentStatus, string> = {
  new: "New",
  active: "Active",
  hospital: "Hospital",
  discharged: "Discharged",
};

// 私 (private) | 券 (voucher) | 買 (bought place) — matches the API enum.
export const RESIDENT_SUBSIDIES = ["private", "voucher", "bought"] as const;
export type ResidentSubsidy = (typeof RESIDENT_SUBSIDIES)[number];

export const RESIDENT_SUBSIDY_LABELS: Record<ResidentSubsidy, string> = {
  private: "私",
  voucher: "券",
  bought: "買",
};

/** Semantic chip class for a resident status (Residents cards + quick view). */
export function residentStatusChipClass(status: string): string {
  if (status === "discharged") return "chip-danger";
  if (status === "hospital") return "chip-warn";
  if (status === "new") return "chip-accent";
  return "";
}

export interface ResidentItem {
  id: string;
  name: string;
  bed: string | null;
  subsidy: ResidentSubsidy;
  homeId: string;
  homeName: string;
  admissionDate: string | null;
  lastAxDate: string | null;
  lastIcpDate: string | null;
  icpFrequency: "1m" | "6m" | "1y" | null;
  nextIcpDate: string | null;
  icpFillStatus: "pending_ax" | "ax_done_awaiting_form" | "form_available" | "filled" | null;
  addedToPaperForm: boolean;
  status: string;
  rowId: string;
  sheetUrl: string | null;
  deepLink: string | null;
  source: string;
  overdue: boolean;
  dueSoon: boolean;
}

export interface UpdateResident {
  icpFrequency?: "1m" | "6m" | "1y" | null;
  lastAxDate?: string | null;
  lastIcpDate?: string | null;
  status?: string;
  addedToPaperForm?: boolean;
  icpFillStatus?: string | null;
}

export interface ResidentFilters {
  homeId?: string | null;
  status?: string;
  /** Gate the query (e.g. wait for another fetch that determines homeId). */
  enabled?: boolean;
}

/** Shared ICP fill-status labels (Dashboard, Residents, quick-view, unified queue). */
const ICP_STATUS_LABELS: Record<Exclude<ResidentItem["icpFillStatus"], null>, string> = {
  pending_ax: "Ax pending",
  ax_done_awaiting_form: "Ax done · waiting for form",
  form_available: "Form ready — fill ICP",
  filled: "Filled",
};

export function icpStatusLabel(status: ResidentItem["icpFillStatus"]): string {
  return status ? (ICP_STATUS_LABELS[status] ?? status) : "";
}

export function useResidents(filters: ResidentFilters = {}) {
  const qc = useQueryClient();
  const homeId = filters.homeId ?? "";
  const status = filters.status ?? "";
  const query = useQuery({
    queryKey: keys.residentsList(homeId, status),
    enabled: filters.enabled ?? true,
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (homeId) qs.set("homeId", homeId);
      if (status) qs.set("status", status);
      return api<{ items: ResidentItem[] }>(`/api/residents?${qs.toString()}`);
    },
    staleTime: 30_000,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateResident }) =>
      api<ResidentItem>(`/api/residents/${id}`, { method: "PATCH", body }),
    onSuccess: () => invalidateAfter(qc, "residentChanged"),
  });
  const sync = useMutation({
    mutationFn: (homeId?: string | null) => api<{ results: { homeName: string; created: number; updated: number; error?: string }[] }>("/api/residents/sync", { method: "POST", body: homeId ? { homeId } : {} }),
    onSuccess: () => invalidateAfter(qc, "residentChanged"),
  });
  return { query, update, sync };
}

/** Manual admission — POST /api/residents; server spawns the admission checklist. */
export function useCreateAdmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { homeId: string; name: string; bed: string; subsidy: ResidentSubsidy; admissionDate: string }) =>
      api<{ resident: ResidentItem; todosCreated: number }>("/api/residents", { method: "POST", body }),
    onSuccess: () => invalidateAfter(qc, "residentChanged"),
  });
}
